import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Sparkles, SlidersHorizontal } from "lucide-react";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function SearchResults() {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [parsed, setParsed] = useState(null);
  const [mobileFilters, setMobileFilters] = useState(false);
  const [topAds, setTopAds] = useState([]);

  useEffect(() => {
    api.get("/campaigns/active", { params: { placement: "SEARCH_TOP" } }).then(({ data }) => setTopAds(data)).catch(() => {});
  }, []);

  const q = params.get("q") || "";
  const category = params.get("category") || "";
  const brand = params.get("brand") || "";
  const maxPrice = params.get("max_price") || "";
  const sort = params.get("sort") || "relevance";

  const runSearch = useCallback(async (overrides = {}) => {
    setLoading(true);
    try {
      const p = { q, sort, ...overrides };
      if (category) p.category = category;
      if (brand) p.brand = brand;
      if (maxPrice) p.max_price = maxPrice;
      const { data } = await api.get("/search", { params: p });
      setData(data);
      if (q) api.post("/track", { type: "search", query: q }).catch(() => {});
    } catch {
      setData({ results: [], count: 0, facets: { categories: [], brands: [] } });
    } finally {
      setLoading(false);
    }
  }, [q, category, brand, maxPrice, sort]);

  useEffect(() => {
    runSearch();
  }, [runSearch]);

  // AI natural-language parsing (background enhancement)
  useEffect(() => {
    if (!q) {
      setParsed(null);
      return;
    }
    if (q.split(" ").length < 3) return;
    let cancelled = false;
    api.post("/search/parse", { query: q }).then(({ data }) => {
      if (cancelled || !data) return;
      const useful = data.category || data.max_price || data.brand;
      setParsed(data);
      if (useful && !category && !maxPrice) {
        const next = new URLSearchParams(params);
        if (data.keywords) next.set("q", data.keywords);
        if (data.category) next.set("category", data.category);
        if (data.max_price) next.set("max_price", String(data.max_price));
        if (data.brand) next.set("brand", data.brand);
        setParams(next, { replace: true });
      }
    }).catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const setParam = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next);
  };

  const filters = (
    <div className="space-y-8">
      <div>
        <p className="text-xs uppercase tracking-widest text-slate-400 font-bold mb-3">Category</p>
        <div className="space-y-1.5">
          {(data?.facets?.categories || []).map((c) => (
            <button
              key={c}
              data-testid={`filter-category-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              onClick={() => setParam("category", category === c ? "" : c)}
              className={`block text-sm ${category === c ? "text-blue-600 font-bold" : "text-slate-600 hover:text-slate-900"}`}
            >
              {c}
            </button>
          ))}
        </div>
      </div>
      <div>
        <p className="text-xs uppercase tracking-widest text-slate-400 font-bold mb-3">Brand</p>
        <div className="space-y-1.5">
          {(data?.facets?.brands || []).map((b) => (
            <button
              key={b}
              data-testid={`filter-brand-${b.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              onClick={() => setParam("brand", brand === b ? "" : b)}
              className={`block text-sm ${brand === b ? "text-blue-600 font-bold" : "text-slate-600 hover:text-slate-900"}`}
            >
              {b}
            </button>
          ))}
        </div>
      </div>
      <div>
        <p className="text-xs uppercase tracking-widest text-slate-400 font-bold mb-3">Max price</p>
        <div className="flex gap-2">
          {[1000, 5000, 20000, 80000].map((v) => (
            <button
              key={v}
              data-testid={`filter-price-${v}`}
              onClick={() => setParam("max_price", maxPrice === String(v) ? "" : String(v))}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${maxPrice === String(v) ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 text-slate-600 hover:border-slate-400"}`}
            >
              ≤₹{v >= 1000 ? `${v / 1000}k` : v}
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div data-testid="search-results-page" className="max-w-7xl mx-auto px-4 sm:px-8 mt-8">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">
            {q ? `Results for "${q}"` : category || "All products"}
          </h1>
          <p className="text-sm text-slate-500 mt-1">{data?.count ?? 0} products from multiple sellers</p>
          {parsed && (parsed.category || parsed.max_price || parsed.color || parsed.size) && (
            <div data-testid="ai-parsed-filters" className="flex items-center gap-2 mt-3 flex-wrap">
              <span className="inline-flex items-center gap-1 text-[11px] font-bold text-blue-600 uppercase tracking-widest"><Sparkles className="w-3 h-3" /> AI understood</span>
              {parsed.category && <span className="text-xs bg-blue-50 text-blue-700 px-2.5 py-1 rounded-full">{parsed.category}</span>}
              {parsed.color && <span className="text-xs bg-blue-50 text-blue-700 px-2.5 py-1 rounded-full">Colour: {parsed.color}</span>}
              {parsed.size && <span className="text-xs bg-blue-50 text-blue-700 px-2.5 py-1 rounded-full">Size {parsed.size}</span>}
              {parsed.max_price && <span className="text-xs bg-blue-50 text-blue-700 px-2.5 py-1 rounded-full">Under ₹{parsed.max_price}</span>}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" className="md:hidden" data-testid="mobile-filters-btn" onClick={() => setMobileFilters(!mobileFilters)}>
            <SlidersHorizontal className="w-4 h-4 mr-2" /> Filters
          </Button>
          <Select value={sort} onValueChange={(v) => setParam("sort", v)}>
            <SelectTrigger data-testid="sort-select" className="w-[180px]"><SelectValue placeholder="Sort" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="relevance">Relevance</SelectItem>
              <SelectItem value="price_asc">Price: low to high</SelectItem>
              <SelectItem value="price_desc">Price: high to low</SelectItem>
              <SelectItem value="rating">Top rated</SelectItem>
              <SelectItem value="discount">Biggest discount</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid md:grid-cols-[220px_1fr] gap-10 mt-8">
        <aside className={`${mobileFilters ? "block" : "hidden"} md:block`} data-testid="filters-panel">{filters}</aside>
        <div>
          {topAds.length > 0 && (
            <div data-testid="search-top-ads" className="mb-6 space-y-3">
              {topAds.map((b) => (
                <a key={b.id} href={b.link_url || "#"} data-testid={`search-ad-${b.id}`}
                  onClick={() => api.post("/track", { type: "campaign_click", campaign_id: b.id }).catch(() => {})}
                  className="flex items-center gap-4 rounded-xl border border-slate-200 p-4 hover:border-blue-300 transition-colors">
                  {b.image_url && <img src={b.image_url.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${b.image_url}` : b.image_url} alt="" className="w-14 h-14 rounded-lg object-cover" />}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm">{b.headline || b.name}</p>
                  </div>
                  <span className="text-[10px] uppercase tracking-widest font-bold text-amber-700 bg-amber-50 px-2.5 py-1 rounded-full shrink-0">
                    {b.kind === "SPONSOR" ? "Sponsored" : "Ad"}
                  </span>
                </a>
              ))}
            </div>
          )}
          {loading ? (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => <div key={i} className="aspect-square rounded-xl bg-slate-100 animate-pulse" />)}
            </div>
          ) : data?.results?.length ? (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-6" data-testid="results-grid">
              {data.results.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
            </div>
          ) : (
            <div data-testid="no-results" className="py-20 text-center rounded-2xl bg-slate-50">
              <p className="font-heading text-xl font-bold">No products found</p>
              <p className="text-sm text-slate-500 mt-2">Try different keywords or remove some filters.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
